import { useEffect } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import FinalCTA from "@/components/landing/FinalCTA";
import FAQ from "@/components/shared/FAQ";
import MedCard from "@/components/shared/MedCard";
import BeforeAfterGallery, { TRANSFORMATIONS } from "@/components/shared/BeforeAfterGallery";
import { Flame, Dumbbell, Activity, Battery, Shield, Wrench, Zap, Gauge, Heart } from "lucide-react";
import SEO from "@/components/shared/SEO";

const MENS_IMG =
  "https://customer-assets.emergentagent.com/job_med-wall-results/artifacts/cbru4hpj_image.png";

const bays = [
  {
    key: "performance",
    label: "PERFORMANCE BAY",
    sub: "Strength · Drive · Output",
    icon: Gauge,
    body: "Testosterone optimization, enclomiphene, and peptide protocols engineered for men who train, build, and push.",
    tags: ["Testosterone", "Enclomiphene"],
  },
  {
    key: "recovery",
    label: "RECOVERY BAY",
    sub: "Sleep · Joints · Bounceback",
    icon: Heart,
    body: "Rebuild what training breaks down. Sleep protocols, peptides, and recovery stacks for harder, longer-lasting work.",
    tags: ["Peptides", "Sleep Rx"],
  },
  {
    key: "weight-loss",
    label: "WEIGHT LOSS BAY",
    sub: "GLP-1 · Cut Without Crash",
    icon: Flame,
    body: "Semaglutide and tirzepatide protocols built for men who want sustainable cuts without losing strength.",
    tags: ["Semaglutide", "Tirzepatide"],
  },
  {
    key: "optimization",
    label: "OPTIMIZATION BAY",
    sub: "Hormones · Longevity · Tune-Up",
    icon: Wrench,
    body: "Full bloodwork, hormone panels, longevity protocols. Treat the body like the machine it is.",
    tags: ["Labs", "HRT"],
  },
];

const categories = [
  { icon: Flame, label: "Weight Loss" },
  { icon: Dumbbell, label: "Testosterone" },
  { icon: Activity, label: "Performance" },
  { icon: Shield, label: "Recovery" },
  { icon: Battery, label: "Energy" },
];

const mensFaq = [
  { q: "Is the Men's Garage right for me?", a: "If you're 25+, want medical guidance on weight, T, performance, recovery, sleep, or hair — yes. We work with men nationwide." },
  { q: "Do I need bloodwork?", a: "For TRT, enclomiphene, and certain weight-loss protocols — yes. We order it, you take it at any LabCorp/Quest." },
  { q: "Is this legal?", a: "Everything is prescribed by a licensed clinician when clinically appropriate. We're a regulated telehealth platform." },
  { q: "How discreet is shipping?", a: "Plain packaging. No mention of BestRx or the medication on the outside." },
  { q: "Can I cancel anytime?", a: "Yes. No contracts, no penalty. Dashboard cancellation in two clicks." },
];

export default function MensGarage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="bg-[var(--leen-bg)] text-white overflow-x-hidden min-h-screen">
      <SEO
        title="The Men's Garage · TRT, Performance & Recovery"
        path="/mens-garage"
        description="Provider-guided men's health: testosterone optimization, enclomiphene, performance medications, peptides, recovery and sleep protocols. Bloodwork-driven care."
      />
      <Nav />

      {/* HERO */}
      <section className="relative pt-16 md:pt-20 min-h-[88vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={MENS_IMG} alt="The Men's Garage" className="img-zoom h-full w-full object-cover object-[30%_center]" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(225deg, rgba(30,122,230,0.55) 0%, rgba(30,122,230,0.15) 35%, rgba(10,14,26,0.55) 100%)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,14,26,0.15) 35%, rgba(10,14,26,0.75) 75%, rgba(10,14,26,0.98) 100%)" }} />
          <div className="absolute top-1/3 left-[15%] w-[340px] h-[340px] rounded-full bg-[var(--leen-blue)]/30 blur-3xl" />
        </div>
        <div className="relative px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto pb-16 md:pb-24 w-full">
          <p className="eyebrow text-[var(--leen-blue)] mb-5 fade-up">For Him · Welcome In</p>
          <p className="font-script text-[var(--leen-blue)] text-[64px] sm:text-[88px] md:text-[110px] lg:text-[140px] leading-none -mb-4 md:-mb-6 lg:-mb-8 italic fade-up fade-up-1">
            The
          </p>
          <h1 className="font-display text-[68px] sm:text-[100px] md:text-[140px] lg:text-[200px] leading-[0.86] tracking-tight text-white fade-up fade-up-1">
            MEN'S<br />
            <span className="text-[var(--leen-blue)]">GARAGE</span>
          </h1>
          <p className="mt-7 font-body text-[18px] md:text-[22px] text-[var(--leen-ink-soft)] max-w-2xl fade-up fade-up-2">
            Engineered protocols in weight loss, testosterone, performance, recovery and energy.
            Built like a garage. Treat your body like the machine it is.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 fade-up fade-up-3">
            <a href="#start-assessment" className="btn-blue">Start Your Assessment <span aria-hidden>→</span></a>
            <a href="#bays" className="btn-ghost">Tour the Bays</a>
          </div>
        </div>
      </section>

      {/* THE BAYS */}
      <section id="bays" className="bg-[var(--leen-bg)] py-20 md:py-28 border-t border-[var(--leen-line)]">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <p className="font-script text-[var(--leen-blue)] text-[44px] md:text-[64px] leading-none italic -mb-2">
                Step into
              </p>
              <h2 className="font-display text-[40px] sm:text-[60px] md:text-[80px] leading-[0.92] text-white">
                CHOOSE YOUR<br /><span className="text-[var(--leen-blue)]">WORKBENCH</span>
              </h2>
            </div>
            <p className="font-body italic text-[15px] text-[var(--leen-ink-soft)] max-w-md">
              Each bay is a curated stack of medications, labs, and clinician care for a specific job.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {bays.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.key} data-testid={`mens-bay-${b.key}`} className="group relative bg-[var(--leen-card)] border border-[var(--leen-line)] hover:border-[var(--leen-blue)] rounded-xl p-7 md:p-9 transition-all hover:-translate-y-1 overflow-hidden">
                  <div className="absolute -top-10 -right-10 w-[180px] h-[180px] rounded-full bg-[var(--leen-blue)]/10 blur-3xl group-hover:bg-[var(--leen-blue)]/25 transition-colors" />
                  <div className="relative">
                    <div className="flex items-center justify-between mb-7">
                      <span className="w-12 h-12 rounded-md bg-[var(--leen-blue)]/15 border border-[var(--leen-blue)]/40 flex items-center justify-center text-[var(--leen-blue)]">
                        <Icon size={20} />
                      </span>
                      <span className="font-num text-[var(--leen-blue)] text-[40px] leading-none opacity-70">{String(bays.indexOf(b) + 1).padStart(2, "0")}</span>
                    </div>
                    <p className="eyebrow text-[var(--leen-blue)] mb-2">{b.sub}</p>
                    <h3 className="font-display text-[28px] md:text-[36px] text-white leading-none mb-5">{b.label}</h3>
                    <p className="text-[14px] md:text-[15px] text-[var(--leen-ink-soft)] leading-relaxed mb-6">{b.body}</p>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {b.tags.map((t) => (
                        <span key={t} className="eyebrow px-3 py-1.5 rounded-full border border-[var(--leen-blue)]/40 text-[var(--leen-blue)]">
                          {t}
                        </span>
                      ))}
                    </div>
                    <a href="#start-assessment" className="eyebrow text-[var(--leen-blue)] inline-flex items-center gap-1.5">
                      Enter Bay <span aria-hidden>→</span>
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CATEGORIES STRIP */}
      <section className="bg-[var(--leen-card)] py-14 md:py-16 border-y border-[var(--leen-line)]">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-5">
            {categories.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.label} data-testid={`mens-cat-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className="flex items-center gap-3 bg-[var(--leen-bg)] border border-[var(--leen-line)] rounded-lg p-4 hover:border-[var(--leen-blue)] transition-colors">
                  <span className="w-10 h-10 rounded-md bg-[var(--leen-blue)]/15 border border-[var(--leen-blue)]/40 flex items-center justify-center text-[var(--leen-blue)] shrink-0">
                    <Icon size={16} />
                  </span>
                  <span className="eyebrow text-white">{c.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MEDICATIONS */}
      <section className="bg-[var(--leen-bg)] py-20 md:py-28">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <div className="text-center mb-14">
            <p className="font-script text-[var(--leen-blue)] text-[44px] md:text-[60px] leading-none italic -mb-2">
              Built for
            </p>
            <h2 className="font-display text-[36px] sm:text-[52px] md:text-[64px] leading-none text-white">
              MEDICATIONS<br /><span className="text-[var(--leen-blue)]">FOR HIM</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            <MedCard slug="testosterone" />
            <MedCard slug="enclomiphene" />
            <MedCard slug="semaglutide" />
            <MedCard slug="tirzepatide" />
            <MedCard slug="l-carnitine" />
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="bg-[var(--leen-card)] py-20 md:py-28 border-y border-[var(--leen-line)]">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <div className="text-center mb-12">
            <p className="font-script text-[var(--leen-blue)] text-[44px] md:text-[60px] leading-none italic -mb-2">
              He did it
            </p>
            <h2 className="font-display text-[36px] sm:text-[52px] md:text-[64px] leading-none text-white mb-3">
              HE GOT TO <span className="text-[var(--leen-blue)]">WORK</span>
            </h2>
            <p className="text-[15px] italic text-[var(--leen-ink-soft)] max-w-xl mx-auto">
              Strength up. Fat down. Confidence rebuilt. Real members. Results vary.
            </p>
          </div>
          <BeforeAfterGallery items={TRANSFORMATIONS.slice(3, 7)} />
          <div className="mt-10 flex justify-center">
            <Link to="/results" className="btn-ghost">See All Transformations <span aria-hidden>→</span></Link>
          </div>
        </div>
      </section>

      <FAQ items={mensFaq} accent="blue" title="MEN'S GARAGE · FAQ" />
      <FinalCTA />
      <Footer />
    </main>
  );
}
